import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAdminApi, User, Company } from "@/hooks/useAdminApi";
import { Plus, Users, Pencil, Trash2, Search, Loader2, Key, Circle, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function AdminUsers() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { toast } = useToast();
  const { listUsers, listCompanies, createUser, updateUser, resetUserPassword, deleteUser } = useAdminApi();
  
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [tempPassword, setTempPassword] = useState("");
  
  const [formEmail, setFormEmail] = useState("");
  const [formRole, setFormRole] = useState<string>("user");
  const [formCompanyId, setFormCompanyId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const roleBadgeVariants: Record<string, "default" | "secondary" | "outline"> = {
    super_admin: "default",
    company_admin: "secondary",
    user: "outline",
  };

  const loadUsers = async () => {
    setLoading(true);
    const result = await listUsers({ 
      page, 
      limit, 
      search,
      company_id: filterCompany !== "all" ? filterCompany : undefined
    });
    if (result.success && result.data) {
      setUsers(result.data);
      setTotal(result.total || 0);
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('users.errorLoadUsers'),
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const loadCompanies = async () => {
    const result = await listCompanies({ limit: 100 });
    if (result.success && result.data) {
      setCompanies(result.data);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [page, search, filterCompany]);

  const resetForm = () => {
    setFormEmail("");
    setFormRole("user");
    setFormCompanyId("");
  };

  const handleCreate = async () => {
    if (!formEmail.trim()) return;
    if (formRole !== "super_admin" && !formCompanyId) {
      toast({
        title: tc('error'),
        description: t('users.errorSelectCompany'),
        variant: "destructive",
      });
      return;
    }
    
    setSubmitting(true);
    const result = await createUser({
      email: formEmail,
      role: formRole,
      company_id: formRole === "super_admin" ? undefined : formCompanyId,
    });
    
    if (result.success) {
      setTempPassword(result.temp_password || "");
      setPasswordDialogOpen(true);
      setCreateDialogOpen(false);
      resetForm();
      loadUsers();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('users.errorCreate'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const handleEdit = async () => {
    if (!selectedUser) return;
    
    setSubmitting(true);
    const result = await updateUser(selectedUser.id, {
      email: formEmail,
      role: formRole,
      company_id: formRole === "super_admin" ? undefined : formCompanyId || undefined,
    });
    
    if (result.success) {
      toast({ title: tc('success'), description: t('users.userUpdated') });
      setEditDialogOpen(false);
      setSelectedUser(null);
      resetForm();
      loadUsers();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('users.errorUpdate'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (user: User) => {
    const result = await updateUser(user.id, { is_active: !user.is_active });
    
    if (result.success) {
      toast({ 
        title: tc('success'), 
        description: user.is_active ? t('users.userDeactivated') : t('users.userActivated')
      });
      loadUsers();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('users.errorChangeStatus'),
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async (user: User) => {
    setSubmitting(true);
    const result = await resetUserPassword(user.id);
    
    if (result.success) {
      setTempPassword(result.temp_password || "");
      setPasswordDialogOpen(true);
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('users.errorResetPassword'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    setSubmitting(true);
    const result = await deleteUser(selectedUser.id);
    
    if (result.success) {
      toast({ title: tc('success'), description: t('users.userDeleted') });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } else {
      toast({
        title: tc('error'),
        description: result.error || t('users.errorDelete'),
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormCompanyId(user.company_id || "");
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: tc('copied'), description: t('users.passwordCopied') });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('users.title')}</h1>
            <p className="text-muted-foreground">
              {t('users.subtitle')}
            </p>
          </div>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            {t('users.addUser')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('users.userList')} ({total})
              </CardTitle>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Select value={filterCompany} onValueChange={(v) => { setFilterCompany(v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder={t('users.filterByCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('users.allCompanies')}</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t('users.searchPlaceholder')}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {search || filterCompany !== "all" ? t('users.noResults') : t('users.noUsers')}
              </p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('users.email')}</TableHead>
                      <TableHead>{t('users.role')}</TableHead>
                      <TableHead>{t('users.company')}</TableHead>
                      <TableHead className="text-center">{t('users.status')}</TableHead>
                      <TableHead className="text-center">{t('users.session')}</TableHead>
                      <TableHead className="text-right">{t('users.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.email}
                          {user.must_change_password && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {t('users.changePassword')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariants[user.role] || "outline"}>
                            {t(`users.roles.${user.role}`, { defaultValue: user.role })}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.company_name || "-"}</TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() => handleToggleActive(user)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Circle 
                            className={`h-3 w-3 mx-auto ${user.has_active_session ? 'fill-green-500 text-green-500' : 'fill-muted text-muted'}`} 
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleResetPassword(user)}
                              title={t('users.resetPassword')}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(user)}
                              title={tc('edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(user)}
                              title={tc('delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      {tc('page')} {page} {tc('of')} {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        {tc('previous')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        {tc('next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.newUser')}</DialogTitle>
            <DialogDescription>
              {t('users.newUserDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('users.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t('users.role')}</Label>
              <Select value={formRole} onValueChange={setFormRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">{t('users.roles.super_admin')}</SelectItem>
                  <SelectItem value="company_admin">{t('users.roles.company_admin')}</SelectItem>
                  <SelectItem value="user">{t('users.roles.user')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formRole !== "super_admin" && (
              <div className="space-y-2">
                <Label htmlFor="company">{t('users.company')}</Label>
                <Select value={formCompanyId} onValueChange={setFormCompanyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('users.selectCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {tc('cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={!formEmail.trim() || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tc('create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.editUser')}</DialogTitle>
            <DialogDescription>
              {t('users.editUserDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">{t('users.email')}</Label>
              <Input
                id="edit-email"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">{t('users.role')}</Label>
              <Select value={formRole} onValueChange={setFormRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">{t('users.roles.super_admin')}</SelectItem>
                  <SelectItem value="company_admin">{t('users.roles.company_admin')}</SelectItem>
                  <SelectItem value="user">{t('users.roles.user')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formRole !== "super_admin" && (
              <div className="space-y-2">
                <Label htmlFor="edit-company">{t('users.company')}</Label>
                <Select value={formCompanyId} onValueChange={setFormCompanyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('users.selectCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {tc('cancel')}
            </Button>
            <Button onClick={handleEdit} disabled={!formEmail.trim() || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tc('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.deleteUser')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('users.deleteUserDesc', { email: selectedUser?.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tc('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.tempPassword')}</DialogTitle>
            <DialogDescription>
              {t('users.tempPasswordDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
              <code className="flex-1 text-lg font-mono">{tempPassword}</code>
              <Button variant="ghost" size="icon" onClick={() => copyToClipboard(tempPassword)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setPasswordDialogOpen(false); setTempPassword(""); }}>
              {tc('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
